import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FiChevronLeft, FiUploadCloud, FiCheck, FiX } from 'react-icons/fi';
import useKYCStore from '../../store/kycStore';
import IconWrapper from '../print-steps/IconWrapper';
import { successToastStyle, errorToastStyle } from '../../utils/toastConfig';

interface KYCDocument {
  id: string;
  file: File;
  preview: string;
  type: 'aadhaar' | 'pan_card' | 'bank_proof' | 'passport_photo';
  uploaded: boolean;
  path?: string;
}

const DocumentUploadStep: React.FC = () => {
  const { 
    documents, 
    addDocument, 
    removeDocument, 
    prevStep, 
    nextStep,
    submitKYCForm,
    isSubmitting,
    uploadProgress,
    setUploadProgress,
    formData
  } = useKYCStore();
  
  const [errors, setErrors] = useState({
    aadhaar: '',
    pan_card: '',
    bank_proof: '',
    passport_photo: ''
  });

  // Define which documents are required based on the payment method
  const hasBankDetails = !!(formData.account_number && formData.ifsc_code);
  
  const requiredDocuments = [
    { 
      type: 'aadhaar' as const, 
      title: 'Aadhaar Card', 
      description: 'Upload clear scan or photo of Aadhaar card (front and back combined in one image/PDF)',
      accept: '.jpg, .jpeg, .png, .pdf',
      required: true // Always required
    },
    { 
      type: 'pan_card' as const, 
      title: 'PAN Card', 
      description: 'Upload clear scan or photo of PAN card',
      accept: '.jpg, .jpeg, .png, .pdf',
      required: false // Optional
    },
    { 
      type: 'bank_proof' as const, 
      title: 'Bank Proof', 
      description: 'Upload bank statement, cancelled cheque or bank passbook first page',
      accept: '.jpg, .jpeg, .png, .pdf',
      required: hasBankDetails // Required only if bank details provided
    },
    { 
      type: 'passport_photo' as const, 
      title: 'Passport Size Photo', 
      description: 'Upload latest passport size photograph with white background',
      accept: '.jpg, .jpeg, .png',
      required: true // Always required
    }
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: KYCDocument['type']) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = type === 'passport_photo' 
      ? ['image/jpeg', 'image/png', 'image/jpg'] 
      : ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ 
        ...prev, 
        [type]: 'Invalid file format. Please upload a valid document.' 
      }));
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrors(prev => ({ 
        ...prev, 
        [type]: 'File is too large. Maximum size is 5MB.' 
      }));
      return;
    }
    
    // Clear error if any
    setErrors(prev => ({ ...prev, [type]: '' }));
    
    // Add document to store
    addDocument(file, type);
  };

  const getDocumentByType = (type: KYCDocument['type']) => {
    return documents.find(doc => doc.type === type);
  };

  const validateDocuments = () => {
    const newErrors = {
      aadhaar: '',
      pan_card: '',
      bank_proof: '',
      passport_photo: ''
    };
    
    let isValid = true;
    
    // Check if all required documents are uploaded
    requiredDocuments.forEach(doc => {
      if (doc.required && !getDocumentByType(doc.type)) {
        newErrors[doc.type] = `${doc.title} is required`;
        isValid = false;
      }
    });
    
    // Special validation for bank proof
    if (hasBankDetails && !getDocumentByType('bank_proof')) {
      newErrors.bank_proof = 'Bank proof is required when bank details are provided';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateDocuments()) {
      toast.error('Please upload all required documents', errorToastStyle);
      return;
    }
    
    setUploadProgress(0);
    const success = await submitKYCForm();
    
    if (success) {
      toast.success('KYC submission successful!', successToastStyle);
      nextStep();
    } else {
      toast.error('Failed to submit KYC form. Please try again.', errorToastStyle);
    }
  };

  // Helper function to truncate filenames
  const truncateFilename = (filename: string, maxLength: number = 25): string => {
    if (!filename) return 'Document';
    if (filename.length <= maxLength) return filename;
    
    const extension = filename.lastIndexOf('.') > -1 
      ? filename.substring(filename.lastIndexOf('.')) 
      : '';
      
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.') > -1 ? filename.lastIndexOf('.') : filename.length);
    
    // Keep the extension intact and truncate the name part
    const truncatedName = nameWithoutExt.substring(0, maxLength - 3 - extension.length) + '...';
    return truncatedName + extension;
  };

  return (
    <div className="max-w-2xl mx-auto px-2">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6">Upload Documents</h2>
      
      <div className="bg-yellow-50 p-3 sm:p-4 rounded-md mb-4 sm:mb-6">
        <p className="text-xs sm:text-sm text-yellow-700">
          Please upload clear, legible copies of all the required documents. All files should be in JPG, PNG, or PDF format and maximum 5MB in size.
        </p>
      </div>
      
      <div className="space-y-4 sm:space-y-6">
        {requiredDocuments.map((doc) => {
          const uploadedDoc = getDocumentByType(doc.type);
          
          return (
            <div key={doc.type} className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 p-3 sm:p-4 border-b">
                <h3 className="font-medium text-sm sm:text-base text-gray-900">
                  {doc.title} {doc.required && <span className="text-red-500">*</span>}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">{doc.description}</p>
                {doc.type === 'bank_proof' && (
                  <p className="text-xs text-blue-600 mt-1">
                    {hasBankDetails 
                      ? "Required since you've provided bank account details" 
                      : "Optional since you're using UPI for payments"}
                  </p>
                )}
                {doc.type === 'pan_card' && (
                  <p className="text-xs text-blue-600 mt-1">
                    Optional document
                  </p>
                )}
              </div>
              
              <div className="p-4">
                {uploadedDoc ? (
                  <div className="flex items-start space-x-4">
                    <div className="flex-grow">
                      {uploadedDoc.preview && (
                        <div className="mb-3">
                          {uploadedDoc.file && uploadedDoc.file.type && uploadedDoc.file.type.startsWith('image/') ? (
                            <img 
                              src={uploadedDoc.preview} 
                              alt={doc.title} 
                              className="h-32 object-cover rounded border border-gray-200" 
                            />
                          ) : (
                            <div className="h-32 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                              <div className="text-center">
                                <IconWrapper icon={FiCheck} className="mx-auto h-8 w-8 text-green-500" />
                                <span className="block mt-1 text-sm text-gray-600">
                                  PDF Document Uploaded
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center">
                        <IconWrapper icon={FiCheck} className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span 
                          className="text-sm font-medium text-gray-900 truncate max-w-[65%] sm:max-w-[75%]" 
                          title={uploadedDoc.file?.name || 'Document'}
                        >
                          {uploadedDoc.file ? truncateFilename(uploadedDoc.file.name) : 'Document'}
                        </span>
                        {uploadedDoc.file && (
                          <span className="ml-2 text-sm text-gray-500">
                            ({(uploadedDoc.file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(uploadedDoc.id)}
                      className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      aria-label="Remove file"
                    >
                      <IconWrapper icon={FiX} className="h-4 w-4" />
                      <span className="sr-only">Remove file</span>
                    </button>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor={`file-upload-${doc.type}`}
                      className="group relative flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer hover:bg-gray-50"
                    >
                      <div className="text-center">
                        <IconWrapper icon={FiUploadCloud} className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-1 text-sm text-gray-600">
                          <span className="font-medium text-blue-600 hover:text-blue-500">Click to upload</span> or drag and drop
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {doc.accept}
                        </p>
                      </div>
                    </label>
                    <input
                      id={`file-upload-${doc.type}`}
                      name={`file-upload-${doc.type}`}
                      type="file"
                      className="sr-only"
                      accept={doc.accept}
                      onChange={(e) => handleFileChange(e, doc.type)}
                    />
                  </div>
                )}
                {errors[doc.type] && <p className="mt-2 text-sm text-red-600">{errors[doc.type]}</p>}
              </div>
            </div>
          );
        })}
        
        {/* Navigation and Submit Buttons */}
        <div className="flex justify-between pt-4 sm:pt-6">
          <button
            type="button"
            onClick={prevStep}
            disabled={isSubmitting}
            className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <IconWrapper icon={FiChevronLeft} className="-ml-1 mr-1 sm:mr-2 h-4 w-4" />
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center px-3 sm:px-6 py-2 border border-transparent shadow-sm text-xs sm:text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {uploadProgress > 0 ? `Uploading (${uploadProgress}%)` : 'Submitting...'}
              </>
            ) : (
              'Submit KYC Information'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadStep;